import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Flame, Phone, UtensilsCrossed, Soup, Beef } from "lucide-react";

const ICONS = [Flame, Soup, Beef];

export default function WeekendSpecials() {
  const { t } = useLanguage();
  const items = t("weekend.items");

  return (
    <section
      id="weekend"
      data-testid="weekend-section"
      className="relative bg-[#C05A3A] py-20 md:py-28 text-[#F9F6F0] overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#A54C30] opacity-50 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-[360px] h-[360px] rounded-full bg-[#E6A85C] opacity-20 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <span className="text-[12px] uppercase tracking-[0.22em] text-[#E6A85C] font-medium inline-flex items-center gap-2">
              <UtensilsCrossed className="h-4 w-4" strokeWidth={1.5} />
              {t("weekend.eyebrow")}
            </span>
            <h2
              data-testid="weekend-title"
              className="mt-4 font-serif-display italic text-[#F9F6F0] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
            >
              {t("weekend.title")}
            </h2>
            <p className="mt-4 max-w-xl text-[#F9F6F0]/85 text-base leading-relaxed">
              {t("weekend.subtitle")}
            </p>
          </div>
          <a
            data-testid="weekend-call-cta"
            href="tel:+12059837363"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#F9F6F0] text-[#C05A3A] hover:bg-[#E6A85C] hover:text-[#232321] px-6 py-3 text-[13px] font-semibold tracking-wide transition-all hover:-translate-y-0.5 shadow-md"
          >
            <Phone className="h-4 w-4" strokeWidth={2} />
            {t("weekend.cta")}
          </a>
        </div>

        <div className="grid grid-cols-2 gap-4 md:gap-6 mb-10 md:mb-12">
          {["/images/caldo-res.jpg", "/images/caldo-verduras.jpg"].map((src, i) => (
            <div
              key={src}
              data-testid={`weekend-photo-${i}`}
              className="relative aspect-[4/3] md:aspect-[16/10] rounded-2xl overflow-hidden border border-[#F9F6F0]/15 shadow-md group"
            >
              <img
                src={src}
                alt="Caldos de fin de semana"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {items.map((it, i) => {
            const Icon = ICONS[i] || Soup;
            return (
              <article
                key={it.name}
                data-testid={`weekend-item-${i}`}
                className="relative rounded-2xl bg-[#A54C30]/35 backdrop-blur-sm border border-[#F9F6F0]/15 p-7 md:p-8 group hover:border-[#E6A85C]/60 transition-colors"
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="h-14 w-14 rounded-full bg-[#F9F6F0]/10 border border-[#F9F6F0]/20 flex items-center justify-center">
                    <Icon className="h-7 w-7 text-[#E6A85C]" strokeWidth={1.4} />
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[#E6A85C] rounded-full bg-[#F9F6F0]/10 border border-[#F9F6F0]/15 px-3 py-1.5">
                    {t("weekend.day")}
                  </span>
                </div>

                <h3 className="font-serif-display text-[#F9F6F0] text-3xl md:text-4xl tracking-tight leading-[1.05]">
                  {it.name}
                </h3>

                <p className="mt-3 text-[#F9F6F0]/80 text-[15px] leading-relaxed">
                  {it.desc}
                </p>

                <div className="mt-6 pt-5 border-t border-[#F9F6F0]/15 flex items-baseline justify-between">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[#F9F6F0]/55">
                    {i === 0 ? "Bowl" : i === 1 ? "Tazón" : "Caldo"}
                  </span>
                  <span className="font-serif-display text-[#F9F6F0] text-2xl">{it.price}</span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
